import { useState, useEffect, useRef, useCallback } from 'react';

const STATUS = { CONNECTED: 'connected', CONNECTING: 'connecting', DISCONNECTED: 'disconnected' };

/**
 * useRealtimeSync — SSE-based real-time sync hook.
 * Connects to /api/events/stream after login, auto-reconnects on failure.
 */
export function useRealtimeSync({ enabled = false, onSpaceUpdate, onChargeUpdate, onConfigUpdate, onSystemReset }) {
  const [status, setStatus] = useState(STATUS.DISCONNECTED);
  const [lastEvent, setLastEvent] = useState(null);
  const eventSourceRef = useRef(null);
  const mountedRef = useRef(true);
  const handlersRef = useRef({ onSpaceUpdate, onChargeUpdate, onConfigUpdate, onSystemReset });

  // Keep handlers ref current without re-triggering effect
  useEffect(() => {
    handlersRef.current = { onSpaceUpdate, onChargeUpdate, onConfigUpdate, onSystemReset };
  }, [onSpaceUpdate, onChargeUpdate, onConfigUpdate, onSystemReset]);

  const connect = useCallback(() => {
    if (!enabled || !mountedRef.current) return;

    // Close any existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    setStatus(STATUS.CONNECTING);

    // EventSource sends cookies automatically on same-origin
    const es = new EventSource('/api/events/stream');
    eventSourceRef.current = es;

    es.onopen = () => {
      if (!mountedRef.current) return;
      console.log('[SSE] Connected');
      setStatus(STATUS.CONNECTED);
    };

    es.onmessage = (event) => {
      if (!mountedRef.current) return;
      try {
        const data = JSON.parse(event.data);
        setLastEvent(data);

        const h = handlersRef.current;
        switch (data.event_type) {
          case 'space_update':
          case 'spaces_regenerated':
            h.onSpaceUpdate?.();
            break;
          case 'charge_update':
            h.onChargeUpdate?.();
            break;
          case 'config_update':
            h.onConfigUpdate?.(data.entity_type);
            break;
          case 'system_reset':
            h.onSystemReset?.();
            break;
          default:
            break;
        }
      } catch {
        // keepalive comments or parse errors — ignore
      }
    };

    es.onerror = () => {
      if (!mountedRef.current) return;

      // EventSource auto-reconnects when readyState is CONNECTING (0)
      // Only show "reconnecting" — don't close or manually reconnect
      if (es.readyState === EventSource.CONNECTING) {
        setStatus(STATUS.CONNECTING);
      } else if (es.readyState === EventSource.CLOSED) {
        // Permanently closed — unlikely, but handle it
        setStatus(STATUS.DISCONNECTED);
      }
    };
  }, [enabled]);

  useEffect(() => {
    mountedRef.current = true;
    if (enabled) {
      connect();
    }
    return () => {
      mountedRef.current = false;
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, [enabled, connect]);

  return { status, lastEvent };
}

/**
 * ConnectionIndicator — green/yellow/red dot showing realtime status
 */
export function ConnectionIndicator({ status }) {
  const colors = {
    [STATUS.CONNECTED]: 'bg-[#4ADE80]',
    [STATUS.CONNECTING]: 'bg-[#FCD34D] animate-pulse',
    [STATUS.DISCONNECTED]: 'bg-[#EF4444]'
  };
  const labels = {
    [STATUS.CONNECTED]: 'En vivo',
    [STATUS.CONNECTING]: 'Reconectando...',
    [STATUS.DISCONNECTED]: 'Desconectado'
  };

  return (
    <div className="flex items-center gap-1.5" data-testid="realtime-status" title={labels[status]}>
      <div className={`w-2 h-2 rounded-full ${colors[status]}`} />
      <span className="text-[10px] text-[#A1A1AA] hidden sm:inline">{labels[status]}</span>
    </div>
  );
}

export { STATUS };
